import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {faInfoCircle, faTimes} from "@fortawesome/free-solid-svg-icons";
import FormUtils from "../../utils/form-utils";
import {Property} from "../../model/property";
import {PricingUtils} from "../../utils/pricing-utils";

@Component({
  selector: 'app-folio-number',
  templateUrl: './folio-number.component.html',
  styleUrls: ['./folio-number.component.css']
})
export class FolioNumberComponent implements OnChanges {

  researchAmount: number;
  folioNumberForm: FormGroup;

  @Input() property: Property
  @Input() displayAdditionalCostInformation: boolean

  protected readonly faInfoCircle = faInfoCircle;
  protected readonly faTimes = faTimes;
  protected readonly FormUtils = FormUtils;

  constructor(private readonly formBuilder: FormBuilder) {
  }

  ngOnChanges(changes:SimpleChanges): void {
    if(changes['property']?.currentValue){
      this.researchAmount = PricingUtils.getResearchAmount();
      const applyValidators = this.property?.dataResearch === false;

      if(!this.folioNumberForm){
        this.folioNumberForm = this.formBuilder.group({
          numbers: this.formBuilder.array(this.buildNumberControls(applyValidators)),
          dataResearch: [this.property?.dataResearch ?? false]
        });
      }

      this.setDataResearchControl();

      // React to dataResearch changes
      this.folioNumberForm.get('dataResearch')?.valueChanges.subscribe((checked: boolean) => {
        // Enable validators when checkbox is unchecked
        this.updateNumberFieldValidators(!checked);
      });
    }

  }

  /**
   * Retrieves the 'numbers' form array from the folio number form.
   *
   * @return {FormArray} The form array representing the 'numbers' control within the folio number form.
   */
  get numbers(): FormArray {
    return this.folioNumberForm.get('numbers') as FormArray;
  }

  /**
   * Validates the folio number form and ensures all form inputs are marked if invalid.
   *
   * This method checks whether the `folioNumberForm` is valid. If the form is invalid,
   * it utilizes `FormUtils.markInvalidInputs` to highlight the problematic inputs.
   *
   * @return {boolean} Returns true if the folio number form is valid, otherwise false.
   */
  public isFolioNumberFormValid(): boolean {
    const isFormValid = this.folioNumberForm.valid;
    if (!isFormValid) {
      FormUtils.markInvalidInputs(this.folioNumberForm);
    }
    return isFormValid;
  }

  /**
   * Adds a new number field to the `numbers` array. The method determines
   * whether the field should have validators applied based on the value
   * of the `dataResearch` field in the `folioNumberForm` form group.
   */
  public addNumberField(): void {
    const dataResearch = this.folioNumberForm?.get('dataResearch')?.value;
    const applyValidators = dataResearch === false;

    this.numbers.push(this.createNumberField(applyValidators));
  }

  /**
   * Removes a number from the numbers field at the specified index.
   *
   * @param {number} index - The index of the number to remove in the numbers field.
   */
  public removeNumberField(index: number): void {
    this.numbers.removeAt(index);
  }

  /**
   * Updates the validation rules for the number field controls within the `numbers` form array.
   *
   * @param {boolean} applyValidators - Indicates whether validators should be applied to the number fields. If true, the `Validators.required` validator will be applied; otherwise, all validators will be cleared.
   */
  private updateNumberFieldValidators(applyValidators: boolean): void {
    const numbersArray = this.folioNumberForm.get('numbers') as FormArray;
    numbersArray.controls.forEach(control => {
      const numberControl = control.get('number');
      if (!numberControl) return;

      numberControl.clearValidators();
      if (applyValidators) {
        numberControl.setValidators(Validators.required);
      }

      numberControl.updateValueAndValidity();
    });
  }

  /**
   * Builds an array of FormGroup instances for managing number controls,
   * based on the available land register folios or a default number field.
   *
   * @param {boolean} applyValidators - Determines whether validation rules should be applied to the controls.
   * @return {FormGroup[]} An array of FormGroup objects representing the number controls.
   */
  private buildNumberControls(applyValidators: boolean): FormGroup[] {
    const folios = this.property?.landregisterfolios ?? [];

    return folios.length > 0 ?
        folios.map(folio => this.formBuilder.group({
          number: [folio.number ?? '', applyValidators ? Validators.required : []]
        })) : [this.createNumberField(applyValidators)];
  }

  /**
   * Configures the 'dataResearch' control of the form based on the data source property.
   * If the data source is 'manual', the control will be disabled. Otherwise, it will be enabled.
   */
  private setDataResearchControl(): void {
    const control = this.folioNumberForm.get('dataResearch');
    if (this.property?.dataSource === 'manual') {
      control?.disable();
    } else {
      control?.enable();
    }
  }

  /**
   * Creates a FormGroup containing a number field, with optional validation.
   *
   * @param {boolean} applyValidators - Determines whether validation should be applied to the number field. Defaults to true.
   * @return {FormGroup} A FormGroup instance containing the defined number field and validators (if specified).
   */
  private createNumberField(applyValidators: boolean = true): FormGroup {
    return this.formBuilder.group({
      number: ['', applyValidators ? Validators.required : []]
    });
  }
}
